import { Box, IconButton, Modal, Stack } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import { PhoneField } from "@/src/components/Fields/PhoneField";
import { CheckBox } from "@/src/components/Fields/CheckBox";
import { Button } from "@/src/components/Button";
import React, { FC } from "react";
import { addPhoneNumberToOrder } from "@/src/api/services/order";
import { useProcessInstanceStore } from "@/src/stores/useProcessInstanceStore";
import { sendConsultationRequestForm } from "@/src/api/services/consultationRequest";
import { useContextStore } from "@/src/stores/useContextStore";
import type { ProductType } from "@/src/shared/config/analytics-config";
import { PRODUCT_ANALYTICS } from "@/src/shared/config/productAnalytics";
import Image from "next/image";

interface FormValues {
    phone: string;
    personalDataAgreement: boolean;
}

interface ExitIntentModalProps {
    onClose: () => void;
    onSubmit: () => void;
    isOpen: boolean;
    title: string;
    description: string;
    submitButtonText: string;
}

export const PhoneCaptureModal: FC<ExitIntentModalProps> = ({
     onClose,
     onSubmit: submitHandler,
     isOpen,
     title,
     description,
     submitButtonText,
 })=>{
    const { processInstance } = useProcessInstanceStore();
    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            phone: "",
            personalDataAgreement: false,
        },
    });
    const {
        control,
        handleSubmit,
    } = methods;

    const contextVariables = useContextStore((state) => state.variables);

    const onSubmit = async (data: FormValues) => {
        if (!data.personalDataAgreement) return;
        onClose();
        const orderId = processInstance.order.id;
        await addPhoneNumberToOrder({phoneNumber: data.phone, id: orderId});
        await sendConsultationRequestForm({
            orderNumber: processInstance.order.number,
            phoneNumber: data.phone,
            productName: PRODUCT_ANALYTICS[contextVariables?.['productName']?.value as ProductType ],
        });
        submitHandler();
    };

    return (
        <Modal open={isOpen} onClose={onClose}>
            <FormProvider {...methods}>
                <Box
                    component="form"
                    className="relative flex w-full max-w-[448px] flex-col gap-4 rounded-xl bg-white shadow-xl center-modal"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Box className="px-6 pt-6 pb-6 flex flex-col gap-4 relative">

                        <IconButton
                            onClick={onClose}
                            className="absolute right-[24px] top-[24px] w-[24px] h-[24px]"
                        >
                            <WebShopIcon name="close" className="fill-blue w-[24px] h-[24px]" />
                        </IconButton>

                        <Stack className="flex flex-col gap-4 text-dark-gray items-center">
                            <Box className="p-6 w-[74px] h-[76px] relative">
                                <Image
                                    src="/images/text-us-thank-you-v2.svg"
                                    alt="admin"
                                    fill
                                    quality={60}
                                    style={{ objectFit: "contain" }}
                                />
                            </Box>

                            <Box>
                                <Box className="text-lg font-semibold text-center">
                                    {title}
                                </Box>
                                <Box className="text-[15px] leading-[22px] font-normal text-center">
                                    {description}
                                </Box>
                            </Box>
                        </Stack>

                        <PhoneField
                            label="Номер телефону"
                            control={control}
                            name="phone"
                            autoComplete="phone"
                            required
                            type="PhoneInput"
                        />

                        <CheckBox
                            control={control}
                            name="personalDataAgreement"
                            label="Даю згоду на використання моїх особистих персональних даних."
                            required
                            className="mr-0"
                            formControlLabelProps={{
                                className: "items-start gap-[3px] [&_.MuiCheckbox-root]:p-0 [&_.MuiCheckbox-root]:pt-0",
                                classes: {
                                    label: "text-dark-gray",
                                },
                            }}
                        />

                        <Stack className="flex flex-col gap-3">
                            <Button variant="contained" type="submit">
                                {submitButtonText}
                            </Button>
                        </Stack>

                    </Box>
                </Box>
            </FormProvider>
        </Modal>
    );
}